import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  EyeIcon, 
  PencilIcon, 
  TrashIcon, 
  UserIcon,
  FunnelIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import UserModal from '../components/users/UserModal';
import UserDetailsModal from '../components/users/UserDetailsModal';
import DeleteUserModal from '../components/users/DeleteUserModal';
import { usersService } from '../services/system/users';
import { toast } from 'react-hot-toast';

const Users = () => {
  const { t } = useTranslation();
  const { canAccessModule, user, currentCompany: company, loading: authLoading } = useAuth();
  
  // State management
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Load users data
  const loadUsers = useCallback(async () => {
    if (!canAccessModule('users')) {
      setLoading(false);
      return;
    }
    
    if (!company?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [usersData, statsData] = await Promise.all([
        usersService.getUsers(company.id),
        usersService.getUserStats(company.id)
      ]);
      
      // Debug: Log user data to see lastLogin information
      console.log('🔍 Debug - Users data:', usersData.map(u => ({
        email: u.email,
        lastLogin: u.lastLogin,
        lastLoginFormatted: u.lastLogin ? (u.lastLogin.toDate ? u.lastLogin.toDate().toISOString() : new Date(u.lastLogin).toISOString()) : 'null'
      })));
      
      setUsers(usersData);
      setUserStats(statsData);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error(t('users.load_error'));
    } finally {
      setLoading(false);
    }
  }, [canAccessModule, company?.id, t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Permission checks - Only admin and superadmin can manage users
  const canWriteUsers = () => user?.role === 'admin' || user?.role === 'superadmin';
  const canDeleteUsers = () => user?.role === 'admin' || user?.role === 'superadmin';

  // Handle user actions
  const handleAddUser = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Opening Add User modal');
    setSelectedUser(null);
    setShowUserModal(true);
  };

  const handleEditUser = (userToEdit) => {
    setSelectedUser(userToEdit);
    setShowUserModal(true);
  };

  const handleViewUser = (userToView) => {
    setSelectedUser(userToView);
    setShowDetailsModal(true);
  };

  const handleDeleteUser = (userToDelete) => {
    setSelectedUser(userToDelete);
    setShowDeleteModal(true);
  };

  const handleSaveUser = async (userData) => {
    if (authLoading) {
      toast.error(t('common.loading', 'Please wait while the system loads...'));
      return;
    }
    
    if (!company?.id) {
      toast.error(t('common.company_not_loaded', 'Company information is not available.'));
      return;
    }

    try {
      setActionLoading(true);
      
      if (selectedUser) {
        // Update existing user
        await usersService.updateUser(selectedUser.id, userData);
        toast.success(t('users.user_updated'));
      } else {
        // Create new user
        await usersService.createUser({
          ...userData,
          companyId: company.id
        });
        toast.success(t('users.user_created'));
      }
      
      setShowUserModal(false);
      setSelectedUser(null);
      await loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      if (error.code === 'auth/email-already-in-use') {
        toast.error(t('users.email_already_exists'));
      } else if (error.code === 'auth/weak-password') {
        toast.error(t('users.password_too_weak'));
      } else {
        toast.error(selectedUser ? t('users.update_error') : t('users.create_error'));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDelete = async (userId) => {
    try {
      setActionLoading(true);
      await usersService.deleteUser(userId);
      toast.success(t('users.user_deleted'));
      setShowDeleteModal(false);
      setSelectedUser(null);
      await loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(t('users.delete_error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      setActionLoading(true);
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      
      if (newStatus === 'active') {
        await usersService.activateUser(userId);
        toast.success(t('users.user_activated'));
      } else {
        await usersService.deactivateUser(userId);
        toast.success(t('users.user_deactivated'));
      }
      
      await loadUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error(t('users.status_update_error'));
    } finally {
      setActionLoading(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter(userItem => {
    const matchesSearch = 
      userItem.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userItem.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userItem.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userItem.role?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || userItem.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || userItem.status === statusFilter;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Utility functions
  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      operations: 'bg-blue-100 text-blue-800',
      accounting: 'bg-green-100 text-green-800',
      client: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadgeColor = (status) => {
    return status === 'active' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return t('common.not_available');
    try {
      const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return t('common.not_available');
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
        <div className="ml-3 text-sm text-gray-500">
          {authLoading ? t('common.loading', 'Loading system...') : t('common.loading', 'Loading users...')}
        </div>
      </div>
    );
  }

  if (!canAccessModule('users')) {
    return (
      <div className="text-center py-12">
        <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          {t('common.access_denied')}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('users.no_permission')}
        </p>
      </div>
    );
  }

  if (!company?.id && !authLoading) {
    return (
      <div className="text-center py-12">
        <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          {t('common.company_not_loaded', 'Company information is not available')}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('common.please_refresh', 'Please refresh the page or contact support if the issue persists.')}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 btn btn-primary btn-sm"
        >
          {t('common.refresh', 'Refresh Page')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">


      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('users.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('users.subtitle')}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <button
            onClick={loadUsers}
            className="btn btn-secondary btn-sm flex items-center space-x-2"
            disabled={loading}
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>{t('common.refresh')}</span>
          </button>
          {canWriteUsers() && (
            <button 
              onClick={handleAddUser}
              disabled={authLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              {authLoading ? t('common.loading', 'Loading...') : t('users.add_user')}
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-blue-600">{userStats.total || 0}</div>
            <div className="text-sm text-gray-500">{t('users.total_users')}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-green-600">{userStats.active || 0}</div>
            <div className="text-sm text-gray-500">{t('users.active')}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-red-600">{userStats.admins || 0}</div>
            <div className="text-sm text-gray-500">{t('users.admin_users')}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-yellow-600">{userStats.activeToday || 0}</div>
            <div className="text-sm text-gray-500">{t('users.active_today')}</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={`${t('common.search')} ${t('users.title').toLowerCase()}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex items-center space-x-3">
              <FunnelIcon className="w-4 h-4 text-gray-400" />
              
              {/* Role Filter */}
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="input min-w-[120px]"
              >
                <option value="all">{t('users.all_roles')}</option>
                <option value="admin">{t('users.admin')}</option>
                <option value="operations">{t('users.operations')}</option>
                <option value="accounting">{t('users.accounting')}</option>
                <option value="client">{t('users.client')}</option>
              </select>
              
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input min-w-[120px]"
              >
                <option value="all">{t('users.all_statuses')}</option>
                <option value="active">{t('users.active')}</option>
                <option value="inactive">{t('users.inactive')}</option>
              </select>
            </div>
          </div>
          
          {/* Results count */}
          <div className="mt-3 text-sm text-gray-500">
            {t('users.showing_results', { 
              count: filteredUsers.length, 
              total: users.length 
            })}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-head">{t('users.name')}</th>
                  <th className="table-head">{t('users.email')}</th>
                  <th className="table-head">{t('users.role')}</th>
                  <th className="table-head">{t('users.status')}</th>
                  <th className="table-head">{t('users.last_login')}</th>
                  <th className="table-head">{t('users.created_at')}</th>
                  <th className="table-head">{t('users.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-12">
                      <UserIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-sm font-medium text-gray-900 mb-1">
                        {searchTerm || roleFilter !== 'all' || statusFilter !== 'all' 
                          ? t('users.no_users_found') 
                          : t('users.no_users_yet')
                        }
                      </h3>
                      <p className="text-sm text-gray-500">
                        {searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
                          ? t('users.try_different_filters')
                          : t('users.add_first_user')
                        }
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((userItem) => (
                    <tr key={userItem.id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center space-x-3">
                          <div className="avatar">
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <UserIcon className="w-4 h-4 text-gray-500" />
                            </div>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {userItem.firstName} {userItem.lastName}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {userItem.id.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">{userItem.email}</div>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${getRoleBadgeColor(userItem.role)}`}>
                          {t(`users.${userItem.role}`, userItem.role)}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <span className={`badge ${getStatusBadgeColor(userItem.status)}`}>
                            {t(`users.${userItem.status}`, userItem.status)}
                          </span>
                          {canWriteUsers() && (
                            <button
                              onClick={() => handleToggleUserStatus(userItem.id, userItem.status)}
                              className={`p-1 rounded ${
                                userItem.status === 'active' 
                                  ? 'text-red-600 hover:bg-red-50' 
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={userItem.status === 'active' ? t('users.deactivate') : t('users.activate')}
                              disabled={actionLoading}
                            >
                              {userItem.status === 'active' ? (
                                <XCircleIcon className="w-4 h-4" />
                              ) : (
                                <CheckCircleIcon className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {formatDate(userItem.lastLogin)}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {formatDate(userItem.createdAt)}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewUser(userItem)}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title={t('common.view')}
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          {canWriteUsers() && (
                            <button
                              onClick={() => handleEditUser(userItem)}
                              className="p-1 text-gray-400 hover:text-green-600"
                              title={t('common.edit')}
                              disabled={actionLoading}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          )}
                          {canDeleteUsers() && userItem.id !== user?.uid && (
                            <button
                              onClick={() => handleDeleteUser(userItem)}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title={t('common.delete')}
                              disabled={actionLoading}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modals */}
      <UserModal
        isOpen={showUserModal}
        onClose={() => {
          setShowUserModal(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUser}
        user={selectedUser}
        loading={actionLoading}
        companyId={company?.id}
      />

      <UserDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onEdit={canWriteUsers() ? handleEditUser : null}
      />

      <DeleteUserModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleConfirmDelete}
        user={selectedUser}
        loading={actionLoading}
      />
    </div>
  );
};

export default Users; 